import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Paper,
} from '@mui/material';
import {
  Mic,
  Stop,
  Send,
  Delete,
  PlayArrow,
  Pause,
} from '@mui/icons-material';
import { voiceConfig } from '../config/n8n.config';
import n8nService, { N8nService } from '../services/n8nService';

interface VoiceRecorderProps {
  onTranscriptionComplete?: (transcription: string, tasks: any[]) => void;
  onError?: (error: string) => void;
}

type RecordingState = 'idle' | 'recording' | 'stopped' | 'uploading' | 'processing';

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onTranscriptionComplete,
  onError,
}) => {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcription, setTranscription] = useState<string>('');
  const [error, setError] = useState<string>('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopRecordingRef = useRef<() => void>(() => {});

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecordingRef.current?.();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      setError('');
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: voiceConfig.audio.sampleRate,
          channelCount: voiceConfig.audio.channels,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      // Determine the best MIME type
      let mimeType = voiceConfig.audio.mimeType;
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = voiceConfig.audio.fallbackMimeType;
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm'; // Last fallback
        }
      }

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        
        // Create audio URL for playback
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        setRecordingState('stopped');
        
        // Stop the stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setRecordingState('recording');
      setRecordingDuration(0);

      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 1;
          
          // Auto-stop at max duration
          if (newDuration >= voiceConfig.recording.maxDuration) {
            stopRecording();
          }
          
          return newDuration;
        });
      }, 1000);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.stop();
    }

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, [recordingState]);

  // Update the ref when stopRecording changes
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  // Poll transcription status until completion
  const pollTranscriptionStatus = useCallback(async (transcriptionId: string) => {
    const maxAttempts = 30; // Maximum 30 attempts (30 seconds with 1-second intervals)
    let attempts = 0;

    const poll = async (): Promise<void> => {
      try {
        attempts++;
        console.log(`🔄 Polling attempt ${attempts}/${maxAttempts} for transcription ${transcriptionId}`);

        const result = await n8nService.getTranscriptionStatus(transcriptionId);

        console.log('🔍 Poll result:', result);

        if (result.success && result.data) {
          if (result.data.status === 'completed' && result.data.transcription) {
            // Transcription completed successfully
            console.log('✅ Transcription completed:', result.data.transcription);
            setTranscription(result.data.transcription);
            onTranscriptionComplete?.(result.data.transcription, result.data.extractedTasks || []);
            setRecordingState('idle');
            return;
          } else if (result.data.status === 'failed') {
            // Transcription failed
            setError('Transcription failed');
            onError?.('Transcription failed');
            setRecordingState('stopped');
            return;
          } else if (result.data.status === 'processing') {
            // Still processing, continue polling
            if (attempts < maxAttempts) {
              setTimeout(poll, 1000); // Poll again in 1 second
            } else {
              setError('Transcription timeout');
              onError?.('Transcription timeout');
              setRecordingState('stopped');
            }
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('❌ Polling error:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000); // Retry in 1 second
        } else {
          setError('Failed to get transcription status');
          onError?.('Failed to get transcription status');
          setRecordingState('stopped');
        }
      }
    };

    // Start polling
    poll();
  }, [onTranscriptionComplete, onError]);

  const playAudio = () => {
    if (audioRef.current && audioUrl) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const deleteRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingState('idle');
    setRecordingDuration(0);
    setTranscription('');
    setError('');
    setIsPlaying(false);
  };

  const uploadAndProcess = async () => {
    if (!audioBlob) return;

    try {
      setRecordingState('uploading');
      setError('');

      // Validate audio file
      N8nService.validateAudioFile(audioBlob);

      // Upload to n8n
      const result = await n8nService.uploadVoiceNote(audioBlob, {
        duration: recordingDuration,
        timestamp: new Date().toISOString(),
      });

      setRecordingState('processing');

      // Handle the response
      console.log('🔍 Frontend received result:', result);
      console.log('🔍 Transcription value:', result.transcription);
      console.log('🔍 Status:', result.status);

      if (result.status === 'processing') {
        // Start polling for completion
        console.log('🔄 Transcription in progress, polling for completion...');
        pollTranscriptionStatus(result.transcriptionId);
      } else if (result.transcription) {
        setTranscription(result.transcription);
        onTranscriptionComplete?.(result.transcription, result.extractedTasks || []);
        setRecordingState('idle'); // Reset state after successful processing
      } else {
        setError('No transcription received');
        onError?.('No transcription received');
        setRecordingState('stopped');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process voice note';
      setError(errorMessage);
      onError?.(errorMessage);
      setRecordingState('stopped');
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressValue = (): number => {
    return (recordingDuration / voiceConfig.recording.maxDuration) * 100;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        mx: { xs: 0, sm: 0 },
        borderRadius: { xs: 0, sm: 1 }
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            textAlign: 'center'
          }}
        >
          Voice Note Recorder
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{
              width: '100%',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Recording Controls */}
        <Box
          display="flex"
          gap={{ xs: 1, sm: 2 }}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="center"
          sx={{
            width: '100%'
          }}
        >
          {recordingState === 'idle' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Mic />}
              onClick={startRecording}
              size="large"
              sx={{
                fontSize: { xs: '0.9rem', sm: '1rem' },
                px: { xs: 2, sm: 3 }
              }}
            >
              Start Recording
            </Button>
          )}

          {recordingState === 'recording' && (
            <Button
              variant="contained"
              color="error"
              startIcon={<Stop />}
              onClick={stopRecording}
              size="large"
              sx={{
                fontSize: { xs: '0.9rem', sm: '1rem' },
                px: { xs: 2, sm: 3 }
              }}
            >
              Stop Recording
            </Button>
          )}

          {recordingState === 'stopped' && audioBlob && (
            <>
              <Button
                variant="outlined"
                startIcon={isPlaying ? <Pause /> : <PlayArrow />}
                onClick={playAudio}
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 1 }
                }}
              >
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                startIcon={<Send />}
                onClick={uploadAndProcess}
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 1 }
                }}
              >
                Process
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={deleteRecording}
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 1 }
                }}
              >
                Delete
              </Button>
            </>
          )}

          {(recordingState === 'uploading' || recordingState === 'processing') && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {recordingState === 'uploading' ? 'Uploading...' : 'Processing...'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Recording Duration and Progress */}
        {(recordingState === 'recording' || recordingState === 'stopped') && (
          <Box width="100%" maxWidth={{ xs: '100%', sm: 400 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Chip
                label={formatDuration(recordingDuration)}
                color={recordingState === 'recording' ? 'error' : 'default'}
                variant={recordingState === 'recording' ? 'filled' : 'outlined'}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  height: { xs: 24, sm: 32 }
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Max: {formatDuration(voiceConfig.recording.maxDuration)}
              </Typography>
            </Box>
            
            {recordingState === 'recording' && (
              <LinearProgress 
                variant="determinate" 
                value={getProgressValue()} 
                color={getProgressValue() > 90 ? 'warning' : 'primary'}
              />
            )}
          </Box>
        )}

        {/* Audio Element for Playback */}
        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={() => setIsPlaying(false)}
            style={{ display: 'none' }}
          />
        )}

        {/* Transcription Result */}
        {transcription && (
          <Box width="100%" mt={2}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Transcription:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2 },
                bgcolor: 'grey.50',
                maxHeight: { xs: 150, sm: 200 },
                overflowY: 'auto'
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {transcription}
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default VoiceRecorder;
